import type { AxiosError, AxiosRequestConfig } from 'axios';
import apiV2 from '../api/v2';
import { toDmError } from './dmErrors';
import { buildSendMessageRequest, type SendMessageParams } from './dmMessageRequest';
import { resolveSignedUrl } from './signedUrl';

export type { SendMessageAttachment, SendMessageParams } from './dmMessageRequest';

export interface DmMessageAttachment {
    mimeType: string;
    url: string;
    previewUrl: string | null;
    previewMimeType: string | null;
}

export interface DmMessage {
    id: string;
    text: string;
    sender: 'patient' | 'practice' | 'system';
    senderName: string;
    createdAt: string;
    // Optional: threads cached in sessionStorage before attachments shipped lack the field.
    attachment?: DmMessageAttachment | null;
}

export interface ListMessagesParams {
    limit?: number;
    cursor?: string;
}

export interface ListMessagesResponse {
    messages: DmMessage[];
    nextCursor: string | null;
}

const HTTP_PAYLOAD_TOO_LARGE = 413;
const PAYLOAD_TOO_LARGE_MESSAGE = 'The attachment is too large to send.';
const FULL_PERCENT = 100;

function normalizeAttachment(
    attachment: DmMessageAttachment | null | undefined
): DmMessageAttachment | null {
    const url = resolveSignedUrl(attachment?.url);
    if (!attachment || !url) return null;
    return {
        mimeType: typeof attachment.mimeType === 'string' ? attachment.mimeType : '',
        url,
        previewUrl: resolveSignedUrl(attachment.previewUrl),
        previewMimeType:
            typeof attachment.previewMimeType === 'string' ? attachment.previewMimeType : null
    };
}

function normalizeMessage(message: DmMessage): DmMessage {
    return {
        ...message,
        text: typeof message.text === 'string' ? message.text : '',
        attachment: normalizeAttachment(message.attachment)
    };
}

export async function listMessages(
    patientId: string,
    params?: ListMessagesParams
): Promise<ListMessagesResponse> {
    try {
        const response = await apiV2.get<{ success: boolean; data: ListMessagesResponse }>(
            `/patients/${patientId}/dm/messages`,
            { params }
        );
        const page = response.data.data;
        const messages = Array.isArray(page.messages) ? page.messages : [];
        return { ...page, messages: messages.map(normalizeMessage) };
    } catch (err) {
        throw toDmError(err);
    }
}

interface SendMessageError extends Error {
    status: number | null;
    code: string | null;
}

function errorCode(error: unknown): string | null {
    if (typeof error !== 'object' || error === null) return null;
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' ? code : null;
}

// A 413 can come from the proxy before the API, without the V2 error envelope.
function toSendMessageError(err: unknown): SendMessageError {
    const axiosError = err as AxiosError<{ error?: unknown }>;
    const response = axiosError.response;
    const base =
        response?.status === HTTP_PAYLOAD_TOO_LARGE && !response.data?.error
            ? new Error(PAYLOAD_TOO_LARGE_MESSAGE)
            : toDmError(err);
    return Object.assign(base, {
        status: response?.status ?? null,
        code: errorCode(response?.data?.error) ?? errorCode(axiosError)
    });
}

function uploadConfig(onUploadProgress?: (percent: number) => void): AxiosRequestConfig {
    // Replaces apiV2's JSON default so axios sends the FormData as-is; the browser adds the boundary.
    return {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
            if (!event.total) return;
            onUploadProgress?.(Math.round((event.loaded / event.total) * FULL_PERCENT));
        }
    };
}

export async function sendMessage(
    patientId: string,
    params: SendMessageParams,
    onUploadProgress?: (percent: number) => void
): Promise<DmMessage> {
    try {
        const request = buildSendMessageRequest(params);
        const response = await apiV2.post<{ success: boolean; data: DmMessage }>(
            `/patients/${patientId}/dm/messages`,
            request.body,
            request.multipart ? uploadConfig(onUploadProgress) : undefined
        );
        return normalizeMessage(response.data.data);
    } catch (err) {
        throw toSendMessageError(err);
    }
}
